<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<div class="k-asset-review-comment" :class="comment.parent_comment_id?'k-asset-review-comment-reply':''">
		<div v-if="!editing">
			<div class="k-asset-review-comment-header d-flex align-center">
				<div>Comment{{comment.parent_comment_id?' Reply':''}} by {{comment.author_name}} (<a :href="`mailto:${comment.author_email}`">{{comment.author_email}}</a>)</div>
				<v-spacer/>
				<div v-html="comment_date"></div>
			</div>

			<div class="k-asset-review-comment-body">
				<div class="d-flex">
					<div v-html="comment.text"></div>
					
					<v-spacer/>

					<v-menu v-if="can_edit" bottom left>
						<template v-slot:activator="{on}">
							<v-btn v-on="on" small icon>
								<v-icon small>fas fa-ellipsis-v</v-icon>
							</v-btn>
						</template>

						<v-list>
							<v-list-item @click="edit_comment">
								<v-list-item-icon>
									<v-icon small>fas fa-edit</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Edit</v-list-item-title>
							</v-list-item>

							<v-list-item @click="delete_comment(false)">
								<v-list-item-icon>
									<v-icon small>fas fa-trash</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Delete</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</div>

				<!-- replies -->
				<AssetReviewComment
					v-for="(c) in replies"
					:key="c.id"
					class="mb-2"
					:comment="c"
					:template="template"
					:review_section="review_section"
					@save_comment="save_comment($event, false)"
					@delete_comment="delete_comment(c)"
				/>

				<div class="d-flex mt-1">
					<v-spacer/>
					
					<!-- only allow replies on top-level comments -->
					<v-btn
						v-if="!new_comment && comment.parent_comment_id == ''"
						color="#444"
						x-small 
						text
						dark
						@click="generate_new_comment"
					>
						New Comment Reply<v-icon small class="ml-2">fas fa-comment</v-icon>
					</v-btn>
				</div>

				<div class="ml-8" v-if="new_comment">
					<AssetReviewCommentEditor
						:comment="new_comment"
						@save_comment="save_comment($event)"
						@cancel_comment="new_comment=null"
					/>
				</div>
			</div>
		</div>

		<div v-else>
			<AssetReviewCommentEditor
				:comment="comment"
				editing
				@save_comment="save_comment($event)"
				@cancel_comment="editing = false"
			/>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import AssetReviewComment from './AssetReviewComment.vue'
import AssetReviewCommentEditor from './AssetReviewCommentEditor.vue'

export default {
	name: 'AssetReviewComment',

	components: {AssetReviewComment, AssetReviewCommentEditor},

	props: {
		template: { required: true },
		review_section: { type: Object, required: true },
		comment: { type: Object, required: true },
	},

	data() { return {
		new_comment: null,
		editing: false,
	}},

	computed: {
		...mapState(['site_config', 'user_info']),

		comment_date() {
			return date.format(new Date(this.comment.created_at*1000), 'MMM D, YYYY h:mm A')
		},

		replies() {
			return this.review_section.comments.filter(c => c.parent_comment_id == this.comment.comment_id)
		},

		can_edit() {
			if (this.replies.length) return false
			
			return this.comment.author_id == this.user_info.user_id
		},
	},

	methods: {
		generate_new_comment() {
			this.new_comment = new Asset_Review_Comment({
				comment_id: 'new',
				parent_comment_id: this.comment.comment_id,
				author_email: this.user_info.email,
				author_name: `${this.user_info.first_name} ${this.user_info.last_name}`,
				author_id: this.user_info.user_id,
			})
		},

		edit_comment() {
			this.editing = true
		},

		delete_comment(comment) {
			if (comment) {
				this.$emit('delete_comment', comment)
			} else {
				this.$confirm({
					title: 'Are you sure?',
					text: 'Are you sure you want to delete this comment?',
					acceptText: 'Delete',
					acceptColor: 'red',
				}).then(() => {
					this.$emit('delete_comment', this.comment)
				}).catch(n => {})
			}
		},

		async save_comment(comment, top_level = true) {
			if (top_level) {
				let c = new Asset_Review_Comment(comment)
				// pass comment up to save
				this.$emit('save_comment', c)
			} else {
				this.$emit('save_comment', comment)
			}

			this.editing = false
			this.new_comment = null
		},
	}
}
</script>

<style lang="scss">
.k-asset-review-comment, .k-asset-review-comment-editor {
	font-size: 14px;
	line-height: 18px;
	border-radius: 8px;
	width: 100%;
}

.k-asset-review-comment {
	background-color: $v-amber-lighten-5;
	border: 1px solid $v-amber;
}
.k-asset-review-comment-reply {
	background-color: #fff;
	margin-left: 32px;
    width: calc(100% - 40px);
}

.k-asset-review-comment-editor {
	background-color: #f8f8f8;
	border: 1px solid $v-amber;
}

.k-asset-review-comment-header {
	background-color: $v-amber-lighten-4;
	border-bottom: 1px solid $v-amber;
	padding: 8px;
	border-radius: 8px 8px 0 0;
}

.k-asset-review-comment-body {
	padding: 8px;
}
</style>